import { useDarkMode } from "@/hooks/useDarkMode";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import { hoveredVenueIdAtom, mobileListViewAtom, savedPlaceIdToViewAtom, viewModeAtom } from "@/state/atoms";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import { darkModeStyles, lightModeStyles } from "common/config";
import { Venue, VenuePinType } from "common/types";
import { useAtom } from "jotai";
import React, { useEffect, useRef, useState } from "react";
import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";
import AnalyticsService from "@/services/analytics";
import { cn } from "@/lib/utils";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { getPinIcon } from "common/utils/maps";
import { StandardButton } from "@/components/common/StandardButton";
import YouAre from "@/assets/pins/youAre.svg";
// import VenueLocation from "@/assets/pins/venue.svg";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { useAuth } from "common/hooks";

function MapViewForNearby(props: {
  venueLocation: { latitude: number; longitude: number };
  filteredBusinesses: Venue[];
  venueId: string;
  hidden?: boolean;
  onMarkerClick?: (location: Venue) => void;
}) {
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [viewMode] = useAtom(viewModeAtom);
  const { isMapper } = useAuth();
  const isMapperViewActive = isMapper && viewMode === "mapper";

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [tooltipVenue, setTooltipVenue] = useState<Venue | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const { selectedVenue, setSelectedVenue } = useSelectedVenue();
  const [, setMobileListView] = useAtom(mobileListViewAtom);
  const [hoveredVenueId] = useAtom(hoveredVenueIdAtom);
  const { isMapsLoaded } = useLoadGoogleMaps();
  const isDark = useDarkMode();
  const markerRefs = useRef(new Map());
  const hasInitialFitBoundsRef = useRef(false);

  const {
    location: userLocation,
    // loading: locationLoading,
    // error: locationError,
    // requestUserLocation,
  } = useGeoLocation();

  const handleActiveMarker = (
    marker: string,
    newPosition: google.maps.LatLng,
    location: Venue,
  ) => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(null);
    setTimeout(() => {
      const overlayHeight = 400; // Adjust according to your overlay's height
      // Calculate the pixel offset to move the map
      const scale = Math.pow(2, mapRef.getZoom());
      const worldCoordinateCenter = mapRef
        .getProjection()
        .fromLatLngToPoint(newPosition);
      const pixelOffset = new google.maps.Point(
        0, // No horizontal shift
        overlayHeight / 2 / scale, // Vertical shift
      );

      const newWorldCoordinate = new google.maps.Point(
        worldCoordinateCenter.x - pixelOffset.x,
        worldCoordinateCenter.y - pixelOffset.y,
      );

      const newCenter = mapRef
        .getProjection()
        .fromPointToLatLng(newWorldCoordinate);

      mapRef.panTo(newCenter);
      setMobileListView(true);
      setActiveMarker(marker);
      setSelectedVenue(location);
    }, 100);
  };

  //result of search api
  // const { locations, isLoading } = useHomeVenues();
  const [delayedLocations, setDelayedLocations] = useState<Venue[]>([]);

  useEffect(() => {
    if (props.filteredBusinesses?.length) {
      const finalLocations =
        props.filteredBusinesses.map((e: Venue) => {
          if (e.venuePinType) return e;
          return {
            ...e,
            venuePinType: getPinIcon(e),
          };
        }) || [];

      // Small delay to ensure map is ready
      setTimeout(() => {
        setDelayedLocations(finalLocations);
      }, 100);
    } else {
      // Clear markers when filteredBusinesses is empty
      setDelayedLocations([]);
    }
  }, [props.filteredBusinesses]);

  const locations = delayedLocations;

  // console.log("Map loaded:", isMapsLoaded);
  // console.log("Locations from props:", locations);
  // console.log("Map ref:", mapRef);

  const onMapMoved = () => {
    // console.log("No progresive search on Nearby Map View");
    return;
  };

  const onLoad = React.useCallback(
    function callback(map: google.maps.Map) {
      setMapRef(map);
      // Center map on venue location when first loaded
      if (props.venueLocation) {
        map.panTo({
          lat: props.venueLocation.latitude,
          lng: props.venueLocation.longitude,
        });
        map.setZoom(15);
      }
    },
    [props.venueLocation],
  );

  const onUnmount = React.useCallback(function callback() {
    setMapRef(null);
    hasInitialFitBoundsRef.current = false;
  }, []);

  const showPinIcon = (venue: Venue) => {
    // User view: Only show Orange/Grey pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPin;
      } else {
        return OrangePin;
      }
    }

    // Mapper view: Show all 6 pin types
    switch (venue.venuePinType) {
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      case VenuePinType.Orange:
        return OrangePin;
      case VenuePinType.Grey:
        return GreyPin;
      default:
        return GreyPin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    // User view: Only show Orange/Grey selected pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPinSelected;
      } else {
        return OrangePinSelected;
      }
    }

    // Mapper view: Show all pin types (selected variants only for Grey/Orange)
    switch (venue.venuePinType) {
      case VenuePinType.Grey:
        return GreyPinSelected;
      case VenuePinType.Orange:
        return OrangePinSelected;
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      default:
        return GreyPinSelected;
    }
  };

  useEffect(() => {
    // Debugging output to ensure locations are received
    // console.log("locations", locations);

    console.log("venueLocation", props.venueLocation);
    console.log("venueLocation latitude", props.venueLocation.latitude);
    console.log("venueLocation longitude", props.venueLocation.longitude);

    // Initialize pin counters
    let orangePin = 0;
    let greyPin = 0;

    // Iterate over the locations and count pins
    locations.map((location) => {
      if (location.reviewsDetailsSubmitted > 0) {
        orangePin++; // Count orange pin
      } else {
        greyPin++; // Count grey pin
      }
    });

    // Tracking the search event in Google Analytics and Mixpanel
    // console.log("searchPageParams:", searchPageParams);
    AnalyticsService.trackGAEventHandler("nearbyVenuesListSearchPerformed");
    AnalyticsService.trackMx("nearbyVenuesListSearchPerformed", {
      SearchTerm: "",
      totalResults: locations.length,
      orangePin: orangePin,
      greyPin: greyPin,
    });
    // console.log("inside homeSearchPerformed");
  }, []); // Added dependencies for useEffect

  useEffect(() => {
    // Only fit bounds once on initial load — skip on subsequent filter/sort changes
    if (hasInitialFitBoundsRef.current) return;

    if (locations.length && mapRef && !activeMarker) {
      const bounds = new google.maps.LatLngBounds();

      // Add all location markers to bounds
      locations.forEach((location) => {
        if (location.latitude && location.longitude) {
          bounds.extend(
            new google.maps.LatLng(location.latitude, location.longitude),
          );
        }
      });

      // Add venue Location if available
      if (props.venueLocation) {
        bounds.extend(
          new google.maps.LatLng(
            props.venueLocation.latitude,
            props.venueLocation.longitude,
          ),
        );
      }

      // Only fit bounds if we have valid coordinates
      if (!bounds.isEmpty()) {
        mapRef.fitBounds(bounds);
        hasInitialFitBoundsRef.current = true;
      }
    }
  }, [locations, props.venueLocation, mapRef, activeMarker]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  const mapOptions = {
    keyboardShortcuts: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    ...(isDark
      ? { styles: darkModeStyles } // Add dark mode styles if isDark is true
      : { styles: lightModeStyles }), // Add mapId if isDark is false
  };

  // Escape key handler to return focus to results list
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const mapContainer = document.querySelector('[aria-label="Map view of nearby venues"]');
        if (mapContainer && mapContainer.contains(document.activeElement)) {
          // Move focus to results list
          const resultsPanel = document.getElementById("venue-list-panel");
          if (resultsPanel) {
            resultsPanel.setAttribute("tabindex", "-1");
            resultsPanel.focus();
            resultsPanel.addEventListener("blur", () => {
              resultsPanel.removeAttribute("tabindex");
            }, { once: true });
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block",
      )}
      aria-label="Map view of nearby venues"
      role="region"
    >
      <div className="sr-only">
        Use Tab to navigate map controls. Press Escape to exit map and return to results list.
      </div>
      {isMapsLoaded && (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
          }}
          center={
            props.venueLocation
              ? {
                  lat: props.venueLocation.latitude,
                  lng: props.venueLocation.longitude,
                }
              : undefined
          }
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions}
          onClick={() => setActiveMarker(null)}
          onIdle={() => onMapMoved()}
          aria-label="Interactive map showing nearby venues"
        >
          {locations.map((location, index) => (
            <Marker
              ref={(marker) => {
                // console.log("Marker data:", {
                // id: location.id,
                // lat: location.latitude,
                // lng: location.longitude,
                // name: location.name,
                // selected: selectedVenue?.id === location.id,
                // index
                // });
                if (marker) {
                  markerRefs.current.set(location.id, marker);
                }
              }}
              key={location.id}
              position={{
                lat: location.latitude,
                lng: location.longitude,
              }}
              title=""
              zIndex={
                selectedVenue && selectedVenue.id === location.id ? 1000 : index
              }
              icon={{
                url:
                  selectedVenue && selectedVenue.id === location.id
                    ? getSelectedPinIcon(location)
                    : showPinIcon(location),
                anchor: new google.maps.Point(15, 30),
                size:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : hoveredMarkerId === location.id ||
                        hoveredVenueId === location.id
                      ? new google.maps.Size(42, 42)
                      : new google.maps.Size(30, 30),
                scaledSize:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : hoveredMarkerId === location.id ||
                        hoveredVenueId === location.id
                      ? new google.maps.Size(42, 42)
                      : new google.maps.Size(30, 30),
              }}
              onMouseOver={() => {
                setTooltipVenue(location);
                setHoveredMarkerId(location.id);
              }}
              onMouseOut={() => {
                if (activeMarker !== location.id) {
                  setTooltipVenue(null);
                  setHoveredMarkerId(null);
                }
              }}
              onClick={() => {
                AnalyticsService.trackGAEventHandler(
                  "nearbyVenuesListPickedVenueFromSearchResultsMap",
                );
                AnalyticsService.trackMx(
                  "nearbyVenuesListPickedVenueFromSearchResultsMap",
                  {
                    VenueId: location.id,
                    VenueBrandId: location.brandId,
                    VenueBrandName: location.brandName,
                    VenueAddress: location.address,
                    VenueName: location.name,
                    VenueType: location.businessType,
                  },
                );
                // Use onMarkerClick if provided (scroll to card), otherwise use old behavior
                if (props.onMarkerClick) {
                  // Just call the marker click handler without setting selected venue
                  props.onMarkerClick(location);
                } else {
                  const newPosition = new google.maps.LatLng({
                    lat: location.latitude,
                    lng: location.longitude,
                  });
                  handleActiveMarker(location.id, newPosition, location);
                }
              }}
              options={{
                label: {
                  className: "sr-only",
                  text: `${location.name}, ${location.businessType}, ${location.distance} miles away`,
                },
              }}
            ></Marker>
          ))}

          {userLocation && (
            <Marker
              key={"userLocation"}
              position={{
                lat: userLocation.latitude,
                lng: userLocation.longitude,
              }}
              title=""
              zIndex={10000}
              icon={{
                url: YouAre,
                anchor: new google.maps.Point(15, 30),
                size: new google.maps.Size(30, 30),
                scaledSize: new google.maps.Size(30, 30),
              }}
            ></Marker>
          )}

          {/* <Marker
            key={"venueLocation"}
            position={{
              lat: props.venueLocation.latitude,
              lng: props.venueLocation.longitude,
            }}
            title=""
            zIndex={5000}
            icon={{
              url: VenueLocation,
              anchor: new google.maps.Point(15, 30),
              size: new google.maps.Size(30, 30),
              scaledSize: new google.maps.Size(30, 30),
            }}
          ></Marker> */}

          {/* Hover tooltip */}
          {(() => {
            const displayVenue =
              tooltipVenue ||
              (hoveredVenueId
                ? locations.find((l) => l.id === hoveredVenueId)
                : null);
            return displayVenue ? (
              <OverlayView
                position={{
                  lat: displayVenue.latitude,
                  lng: displayVenue.longitude,
                }}
                mapPaneName={OverlayView.FLOAT_PANE}
                getPixelPositionOffset={(width, height) => ({
                  x: -(width / 2),
                  y: -height - 50,
                })}
              >
                <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-2 rounded-lg shadow-lg pointer-events-none w-max max-w-[400px]">
                  <div className="font-medium label-m-medium whitespace-nowrap">
                    {displayVenue.name}
                  </div>
                </div>
              </OverlayView>
            ) : null;
          })()}
        </GoogleMap>
      )}

      {/* Goto current location, +, - */}
      <div
        className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end"
        aria-label="Map controls"
      >
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            AnalyticsService.trackGAEventHandler(
              "nearbyVenuesListZoomedInMapView",
            );
            AnalyticsService.trackMx("nearbyVenuesListZoomedInMapView", {});
            mapRef.getZoom() < 17 && mapRef.setZoom(mapRef.getZoom() + 1);
          }}
          aria-label="Zoom in"
          className="p-2 mb-1 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold"
        >
          <FaPlus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            AnalyticsService.trackGAEventHandler(
              "nearbyVenuesListZoomedOutMapView",
            );
            AnalyticsService.trackMx("nearbyVenuesListZoomedOutMapView", {});
            mapRef.getZoom() > 0 && mapRef.setZoom(mapRef.getZoom() - 1);
          }}
          aria-label="Zoom out"
          className="p-2 mb-8 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold"
        >
          <FaMinus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
      </div>
    </div>
  );
}

export default MapViewForNearby;
